import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Package, Truck, ShieldCheck } from "lucide-react";
import SpideyLogo from "@/components/SpideyLogo";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebBurst = () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" aria-hidden="true">
        <g stroke="#E60026" strokeWidth="1.5" className="animate-web-draw" strokeLinecap="round">
            {[0, 60, 120].map((deg) => (
                <line key={deg} x1="60" y1="10" x2="60" y2="110" transform={`rotate(${deg} 60 60)`} />
            ))}
            {[0, 60, 120].map((deg) => (
                <line key={`h-${deg}`} x1="10" y1="40" x2="110" y2="80" transform={`rotate(${deg} 60 60)`} />
            ))}
            {[22, 40, 58].map((r) => (
                <circle key={r} cx="60" cy="60" r={r} strokeDasharray="4 6" />
            ))}
        </g>
    </svg>
);

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("pending");
    const { clearCart } = useCart();

    useEffect(() => {
        if (!sessionId) {
            setState("done");
            return;
        }
        let tries = 0;
        const poll = async () => {
            try {
                const { data } = await axios_getStatus(sessionId);
                if (data.payment_status === "paid") {
                    setState("paid");
                    clearCart();
                    return;
                }
            } catch (e) {
                /* réessaie */
            }
            tries += 1;
            if (tries >= 8) {
                setState("done");
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-spidey-bg flex flex-col" data-testid="order-confirmation-success-container">
            <header className="max-w-3xl w-full mx-auto px-5 pt-8">
                <Link to="/">
                    <SpideyLogo />
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-5 py-16">
                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-lg bg-spidey-card border border-zinc-800 rounded-3xl p-10 text-center relative overflow-hidden"
                >
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-spidey-red/20 blur-3xl rounded-full pointer-events-none" />

                    {state === "pending" && (
                        <>
                            <Loader2 size={56} className="mx-auto text-spidey-red animate-spin" />
                            <h1 className="mt-6 font-display uppercase text-3xl text-white">
                                Vérification du paiement...
                            </h1>
                            <p className="mt-3 text-sm text-zinc-500">
                                Quelques secondes, on confirme ta commande.
                            </p>
                        </>
                    )}

                    {state === "paid" && (
                        <>
                            <div className="relative inline-block">
                                <WebBurst />
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="w-14 h-14 rounded-full bg-spidey-red glow-red flex items-center justify-center">
                                        <Check size={28} className="text-white" strokeWidth={3} />
                                    </span>
                                </span>
                            </div>
                            <h1 className="mt-5 font-display uppercase text-4xl text-white">
                                Paiement confirmé
                            </h1>
                            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                                Ta commande est réservée. Un e-mail de confirmation
                                avec ton reçu vient de partir.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-3 text-left">
                                <div className="bg-black/40 border border-zinc-800 rounded-xl p-4">
                                    <Truck size={18} className="text-spidey-red" />
                                    <p className="mt-2 text-xs font-mono-brand uppercase tracking-wider text-zinc-400">
                                        Livraison suivie
                                    </p>
                                    <p className="text-[11px] text-zinc-600 mt-1">E-mail à l'expédition</p>
                                </div>
                                <div className="bg-black/40 border border-zinc-800 rounded-xl p-4">
                                    <Package size={18} className="text-spidey-red" />
                                    <p className="mt-2 text-xs font-mono-brand uppercase tracking-wider text-zinc-400">
                                        Commande
                                    </p>
                                    <p className="text-[11px] text-zinc-600 mt-1 truncate">
                                        {sessionId ? `#${sessionId.slice(-12).toUpperCase()}` : "—"}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {state === "done" && (
                        <>
                            <ShieldCheck size={56} className="mx-auto text-spidey-red" />
                            <h1 className="mt-6 font-display uppercase text-3xl text-white">
                                Commande enregistrée
                            </h1>
                            <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
                                Ton paiement est en cours de confirmation. Tu recevras un
                                e-mail dès qu'il est validé.
                            </p>
                        </>
                    )}

                    <Link
                        to="/"
                        className="mt-9 inline-block bg-spidey-red hover:bg-spidey-neon text-white font-bold uppercase tracking-wider text-sm px-8 py-3.5 rounded-full transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(230,0,38,0.5)]"
                        data-testid="back-to-shop-button"
                    >
                        Retour à la boutique
                    </Link>
                </motion.div>
            </main>
        </div>
    );
}

async function axios_getStatus(sessionId) {
    const res = await fetch(`${API}/payments/status/${sessionId}`);
    if (!res.ok) throw new Error("status unavailable");
    return { data: await res.json() };
}
